/**
 * @fileoverview Share-link encoding for a Custom Mosaics design.
 *
 * Query params:
 * - `p`   pattern id
 * - `g`   grout color id
 * - `pal` palette used by the design: `Name~HEX|Name~HEX` (hex without `#`)
 * - `t`   one code per tile, in ascending tile-id order; each code indexes `pal`,
 *         `-` means unpainted. Codes are 1 char, or 2 chars when `tw=2`.
 * - `tw`  code width (only present when the palette exceeds 62 colors)
 *
 * @module designCodec
 */
import { sortedTileIds } from './patternData.js';

const ALPHABET = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
const UNPAINTED = '-';

/** Query keys owned by the design codec (stripped from the URL after applying). */
export const DESIGN_PARAM_KEYS = ['p', 'g', 'pal', 't', 'tw', 'src'];

function encodeIndex(i, width) {
    if (width === 1) return ALPHABET[i];
    return ALPHABET[Math.floor(i / ALPHABET.length)] + ALPHABET[i % ALPHABET.length];
}

function decodeIndex(code) {
    let n = 0;
    for (const ch of code) {
        const v = ALPHABET.indexOf(ch);
        if (v < 0) return -1;
        n = n * ALPHABET.length + v;
    }
    return n;
}

/**
 * Encodes a design into query params.
 * @param {Object} pattern - Pattern definition
 * @param {Object<string, {name: string, hex: string}>} colors - Tile id → color
 * @param {string} groutId
 * @returns {Object<string, string>}
 */
export function encodeDesign(pattern, colors, groutId) {
    const params = { p: pattern.id };
    if (groutId) params.g = groutId;

    const ids = sortedTileIds(pattern);
    const palette = [];
    const indexByName = new Map();
    for (const id of ids) {
        const c = colors?.[id];
        if (c?.name && !indexByName.has(c.name)) {
            indexByName.set(c.name, palette.length);
            palette.push(c);
        }
    }
    if (palette.length === 0) return params;

    const width = palette.length > ALPHABET.length ? 2 : 1;
    params.pal = palette.map((c) => `${c.name}~${String(c.hex || '').replace('#', '')}`).join('|');
    params.t = ids
        .map((id) => {
            const c = colors?.[id];
            return c?.name ? encodeIndex(indexByName.get(c.name), width) : UNPAINTED.repeat(width);
        })
        .join('');
    if (width === 2) params.tw = '2';
    return params;
}

/**
 * Decodes tile colors for a pattern from query params. Malformed input yields
 * an empty color map rather than throwing.
 * @param {Object} pattern
 * @param {Object<string, string>} query
 * @returns {Object<string, {name: string, hex: string}>}
 */
export function decodeColors(pattern, query) {
    const colors = {};
    if (!pattern || !query?.pal || !query?.t) return colors;

    const palette = String(query.pal)
        .split('|')
        .map((entry) => {
            const sep = entry.lastIndexOf('~');
            const name = sep >= 0 ? entry.slice(0, sep) : entry;
            const hex = sep >= 0 ? entry.slice(sep + 1) : '';
            return /^[0-9a-fA-F]{6}$/.test(hex) && name ? { name, hex: `#${hex}` } : null;
        });

    const width = query.tw === '2' ? 2 : 1;
    const codes = String(query.t);
    const ids = sortedTileIds(pattern);
    if (codes.length !== ids.length * width) return colors;

    ids.forEach((id, i) => {
        const code = codes.slice(i * width, i * width + width);
        if (code === UNPAINTED.repeat(width)) return;
        const c = palette[decodeIndex(code)];
        if (c) colors[id] = { ...c };
    });
    return colors;
}

/**
 * Builds a full share URL.
 * @param {string} baseUrl - origin + pathname
 * @param {Object<string, string>} params
 * @returns {string}
 */
export function buildShareUrl(baseUrl, params) {
    const usp = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v != null && v !== '') usp.set(k, String(v));
    }
    usp.set('src', 'share');
    return `${baseUrl}?${usp.toString()}`;
}
